"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError, loadTokens } from "@/lib/api";
import { MarketEmbedModal } from "@/components/MarketEmbedModal";

/**
 * EPIC-006 STORY-613 + STORY-614 — copy-link + bookmark actions on
 * the market detail page header.
 *
 * Copy link includes a `?r=<encoded-user-id>` referral parameter so
 * future click-tracking (STORY-613 backend phase) can attribute the
 * click back to the sharing user. The encoding is a base64url of the
 * user id; the foundation for the referral-rewards program (EPIC-013
 * STORY-1303 tier upgrades).
 *
 * Bookmark toggles via POST/DELETE /v1/me/bookmarks/:marketId.
 * Optimistic UI flip; rollback on failure. Not visible when the user
 * is logged out — bookmarks are user-scoped state.
 */
export function MarketShareActions({
  marketId,
  marketSlug,
}: {
  marketId: string;
  marketSlug: string;
}) {
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);

  // Probe auth + initial bookmark state. Anonymous viewers see only
  // the Copy link button; the bookmark icon is auth-gated.
  useEffect(() => {
    const tokens = loadTokens();
    if (!tokens) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoggedIn(true);
    void api
      .listBookmarks()
      .then((rows) => {
        setBookmarked(rows.some((r) => r.marketId === marketId));
      })
      .catch(() => {
        // silent — anonymous fallback
      });
  }, [marketId]);

  async function handleCopyLink() {
    const tokens = loadTokens();
    const userId = tokens?.user.id ?? null;
    const url = new URL(window.location.href);
    url.pathname = `/markets/${marketSlug}`;
    url.search = "";
    if (userId) {
      // Base64url-encode the UUID so it's URL-safe + non-trivial to
      // type from memory. Decode happens server-side in the future
      // referral-click endpoint (foundation in migration 0031).
      const encoded = btoa(userId)
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replaceAll("=", "");
      url.searchParams.set("r", encoded);
    }
    try {
      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // older browsers: silent fallback (selection-based copy is
      // not worth the maintenance cost at closed-beta scale)
    }
  }

  async function handleBookmarkToggle() {
    if (!isLoggedIn || pending) return;
    const next = !bookmarked;
    setBookmarked(next); // optimistic
    setPending(true);
    try {
      const result = next
        ? await api.addBookmark(marketId)
        : await api.removeBookmark(marketId);
      setBookmarkCount(result.bookmarkCount);
    } catch (err) {
      // rollback on failure so the UI doesn't lie about server state
      setBookmarked(!next);
      if (err instanceof ApiError && err.status === 401) {
        setIsLoggedIn(false);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <button
        type="button"
        onClick={() => void handleCopyLink()}
        className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1 text-[11px] font-semibold text-[var(--color-text2)] transition-colors hover:border-[var(--color-border2)] hover:text-[var(--color-text)]"
        title={t("marketShare.copyLinkTitle")}
      >
        <span aria-hidden>🔗</span>
        <span>{copied ? t("marketShare.copied") : t("marketShare.copyLink")}</span>
      </button>
      <button
        type="button"
        onClick={() => setEmbedOpen(true)}
        className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1 text-[11px] font-semibold text-[var(--color-text2)] transition-colors hover:border-[var(--color-border2)] hover:text-[var(--color-text)]"
        title={t("marketShare.embedTitle")}
      >
        <span aria-hidden>📰</span>
        <span>{t("marketShare.embed")}</span>
      </button>
      {embedOpen && (
        <MarketEmbedModal
          marketSlug={marketSlug}
          onClose={() => setEmbedOpen(false)}
        />
      )}
      {isLoggedIn ? (
        <button
          type="button"
          disabled={pending}
          onClick={() => void handleBookmarkToggle()}
          className="inline-flex items-center gap-1 rounded-md border bg-[var(--color-surface)] px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50"
          style={{
            borderColor: bookmarked
              ? "var(--color-brand)"
              : "var(--color-border)",
            color: bookmarked
              ? "var(--color-brand)"
              : "var(--color-text2)",
          }}
          title={
            bookmarked
              ? t("marketShare.removeBookmark")
              : t("marketShare.addBookmark")
          }
          aria-pressed={bookmarked}
        >
          <span aria-hidden>{bookmarked ? "★" : "☆"}</span>
          <span>
            {bookmarked
              ? t("marketShare.bookmarked")
              : t("marketShare.bookmark")}
          </span>
          {bookmarkCount !== null && bookmarkCount > 0 ? (
            <span className="ml-1 text-[var(--color-text3)]">
              · {bookmarkCount}
            </span>
          ) : null}
        </button>
      ) : null}
    </div>
  );
}
